import { INDUSTRY_PRESETS, presetFor, type BusinessType, type IndustryPreset, type Lang } from '@app/shared';
import type { CatalogCardDto } from '../api/types';

/**
 * Группировка каталога по отраслям (ТЗ §14.1, прототип `renderCatalog`).
 *
 * Порядок отраслей и групп берётся из `INDUSTRY_PRESETS`, а не переписывается здесь списком:
 * копия разошлась бы с пресетами при добавлении десятой отрасли, и новая отрасль просто
 * не появилась бы в каталоге — молча, без единой ошибки.
 */

export interface CatalogSection {
  readonly type: BusinessType;
  readonly preset: IndustryPreset;
  readonly items: readonly CatalogCardDto[];
}

/**
 * Плитки отраслей (каталог, онбординг) по алфавиту языка интерфейса. Коллатор `uz` знает
 * узбекский алфавит: Oʻ, Gʻ, Sh, Ch идут после Z.
 */
export function industryOptions(lang: Lang): readonly IndustryPreset[] {
  const collator = new Intl.Collator(lang);
  return [...INDUSTRY_PRESETS].sort((a, b) =>
    collator.compare(industryName(a, lang), industryName(b, lang)),
  );
}

/** Название отрасли на языке интерфейса. */
export function industryName(preset: IndustryPreset, lang: Lang): string {
  return lang === 'ru' ? preset.nameRu : preset.nameUz;
}

/**
 * Раскладка карточек по отраслям.
 *
 * Пустые секции не возвращаются: заголовок «Стоматология» без единой строки под ним
 * выглядит как сломанная загрузка, а не как «в городе нет стоматологий».
 *
 * Неизвестная отрасль роняет вызов через `presetFor` — это осознанно (AGENTS.md, правило 7).
 * Отрасль приходит с бэкенда из того же enum, что и пресеты; несовпадение означает, что
 * фронтенд собран старее бэкенда, и тихо спрятать такие организации значило бы показать
 * клиенту неполный каталог, не сообщив об этом никому.
 */
export function groupCatalogByIndustry(
  items: readonly CatalogCardDto[],
  lang: Lang,
): CatalogSection[] {
  const buckets = new Map<BusinessType, CatalogCardDto[]>();
  for (const item of items) {
    // Проверка отрасли до раскладки: иначе неизвестный тип осел бы в своей корзине
    // и «потерялся» бы только на отрисовке.
    presetFor(item.type);
    const bucket = buckets.get(item.type);
    if (bucket) bucket.push(item);
    else buckets.set(item.type, [item]);
  }

  const collator = new Intl.Collator(lang === 'ru' ? 'ru' : 'uz');
  const sections: CatalogSection[] = [];

  for (const preset of INDUSTRY_PRESETS) {
    const bucket = buckets.get(preset.type);
    if (!bucket || bucket.length === 0) continue;
    sections.push({
      type: preset.type,
      preset,
      // Сортировка по названию — как в прототипе (sortByName): порядок выдачи API задан
      // курсором пагинации и внутри отрасли выглядит для человека случайным.
      items: [...bucket].sort((a, b) => collator.compare(a.name, b.name)),
    });
  }

  return sections;
}
